<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import schedulingAPI from '../api/scheduling';

const props = defineProps({
  services: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
  patients: {
    type: Array,
    default: () => [],
  },
  initialData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'saved']);

const isSaving = ref(false);
const isQuickCreating = ref(false);
const errorMessage = ref('');

// Patient State
const patientContainerRef = ref(null);
const patientSearchQuery = ref('');
const showPatientDropdown = ref(false);
const isCreatingNewPatient = ref(false);
const newPatientPhone = ref('');
const newPatientEmail = ref('');

// Weekdays Options
const availableWeekDays = [
  { key: 'monday', label: 'Seg', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'Ter', full: 'Terça-feira' },
  { key: 'wednesday', label: 'Qua', full: 'Quarta-feira' },
  { key: 'thursday', label: 'Qui', full: 'Quinta-feira' },
  { key: 'friday', label: 'Sex', full: 'Sexta-feira' },
  { key: 'saturday', label: 'Sáb', full: 'Sábado' },
];

const selectedDays = ref(['monday']);

// Custom Schedule by Day State
const isCustomScheduleByDay = ref(false);
const scheduleByDay = ref({
  monday: { time_slot: '08:00', professional_id: null },
  tuesday: { time_slot: '08:00', professional_id: null },
  wednesday: { time_slot: '08:00', professional_id: null },
  thursday: { time_slot: '08:00', professional_id: null },
  friday: { time_slot: '08:00', professional_id: null },
  saturday: { time_slot: '08:00', professional_id: null },
});

const getDayLabel = key => {
  const d = availableWeekDays.find(w => w.key === key);
  return d ? d.full : key;
};

// Shifts & Times
const activeShift = ref('morning');
const morningTimes = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonTimes = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightTimes = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const shiftTimes = computed(() => {
  if (activeShift.value === 'morning') return morningTimes;
  if (activeShift.value === 'afternoon') return afternoonTimes;
  return nightTimes;
});

const isTrialClass = ref(false);

const enrollmentType = computed(() => {
  return isTrialClass.value ? 'trial' : (formData.value.session_type === 'recurring' ? 'recurring' : 'package');
});

const formData = ref({
  scheduling_patient_id: null,
  scheduling_service_id: null,
  scheduling_professional_id: null,
  session_type: 'recurring', // 'recurring' | 'package' | 'single'
  due_day: 5,
  time_slot: '08:00',
  start_date: new Date().toISOString().split('T')[0],
  total_sessions: 10,
  price: 0,
  notes: '',
});

const selectedService = computed(() => {
  return props.services.find(s => Number(s.id) === Number(formData.value.scheduling_service_id));
});

const allowsTrialClass = computed(() => {
  return !!selectedService.value?.allow_trial_class;
});

const onToggleTrialClass = () => {
  if (isTrialClass.value) {
    formData.value.session_type = 'single';
    formData.value.total_sessions = 1;
    if (selectedDays.value.length > 1) {
      selectedDays.value = [selectedDays.value[0]];
    }
  } else {
    if (selectedService.value?.billing_type === 'recurring') {
      formData.value.session_type = 'recurring';
    } else if (selectedService.value?.billing_type === 'package') {
      formData.value.session_type = 'package';
    } else {
      formData.value.session_type = 'single';
    }
    formData.value.price = selectedService.value?.price || 0;
  }
};

const isDayAllowedForService = dayKey => {
  if (!selectedService.value?.custom_schedule_enabled) return true;
  const allowed = selectedService.value.allowed_days || [];
  if (allowed.length === 0) return true;
  return allowed.includes(dayKey);
};

const isTimeSlotAllowedForService = slot => {
  if (!selectedService.value?.custom_schedule_enabled) return true;
  const allowed = selectedService.value.allowed_time_slots || [];
  if (allowed.length === 0) return true;
  return allowed.includes(slot);
};

const filteredPatients = computed(() => {
  const query = patientSearchQuery.value.toLowerCase().trim();
  if (!query) return props.patients.slice(0, 10);

  return props.patients.filter(
    p =>
      p.name.toLowerCase().includes(query) ||
      (p.phone_number && p.phone_number.includes(query)) ||
      (p.email && p.email.toLowerCase().includes(query))
  );
});

const exactPatientMatch = computed(() => {
  const query = patientSearchQuery.value.toLowerCase().trim();
  if (!query) return null;
  return props.patients.find(p => p.name.toLowerCase().trim() === query);
});

const selectPatient = p => {
  formData.value.scheduling_patient_id = p.id;
  patientSearchQuery.value = p.name;
  isCreatingNewPatient.value = false;
  showPatientDropdown.value = false;
};

const handlePatientInput = () => {
  showPatientDropdown.value = true;
  const match = exactPatientMatch.value;
  if (match) {
    formData.value.scheduling_patient_id = match.id;
    isCreatingNewPatient.value = false;
  } else {
    formData.value.scheduling_patient_id = null;
    isCreatingNewPatient.value = patientSearchQuery.value.trim().length > 0;
  }
};

const handleQuickCreatePatient = async () => {
  const name = patientSearchQuery.value.trim();
  if (!name) return;

  isQuickCreating.value = true;
  errorMessage.value = '';
  try {
    const res = await schedulingAPI.createPatient({
      name: name,
      phone_number: newPatientPhone.value.trim(),
      email: newPatientEmail.value.trim(),
    });

    const created = res.data;
    formData.value.scheduling_patient_id = created.id;
    patientSearchQuery.value = created.name;
    isCreatingNewPatient.value = false;
    showPatientDropdown.value = false;
  } catch (err) {
    isCreatingNewPatient.value = true;
    showPatientDropdown.value = false;
  } finally {
    isQuickCreating.value = false;
  }
};

const toggleDay = dayKey => {
  if (isTrialClass.value) {
    selectedDays.value = [dayKey];
    return;
  }

  const idx = selectedDays.value.indexOf(dayKey);
  if (idx > -1) {
    if (selectedDays.value.length > 1) {
      selectedDays.value.splice(idx, 1);
    }
  } else {
    selectedDays.value.push(dayKey);
  }
};

const handleClickOutside = event => {
  if (patientContainerRef.value && !patientContainerRef.value.contains(event.target)) {
    showPatientDropdown.value = false;
  }
};

function autoSetShiftFromTime(timeStr) {
  const hour = parseInt(timeStr.split(':')[0], 10);
  if (hour < 12) activeShift.value = 'morning';
  else if (hour < 18) activeShift.value = 'afternoon';
  else activeShift.value = 'night';
}

const filteredProfessionals = computed(() => {
  if (!formData.value.scheduling_service_id) return props.professionals;
  const sId = Number(formData.value.scheduling_service_id);

  return props.professionals.filter(p => {
    if (!p.service_ids || p.service_ids.length === 0) return true;
    return p.service_ids.map(Number).includes(sId);
  });
});

const onServiceChange = () => {
  if (selectedService.value) {
    if (!selectedService.value.allow_trial_class && isTrialClass.value) {
      isTrialClass.value = false;
    }
    if (!isTrialClass.value) {
      if (selectedService.value.billing_type === 'recurring') {
        formData.value.session_type = 'recurring';
      } else if (selectedService.value.billing_type === 'single') {
        formData.value.session_type = 'single';
      } else {
        formData.value.session_type = 'package';
      }
      formData.value.price = selectedService.value?.price || 0;
    }
  }

  // Auto-ajusta o profissional conforme os que estão habilitados para o serviço
  const validProfIds = filteredProfessionals.value.map(p => p.id);
  if (formData.value.scheduling_professional_id && !validProfIds.includes(formData.value.scheduling_professional_id)) {
    formData.value.scheduling_professional_id = validProfIds.length === 1 ? validProfIds[0] : null;
  } else if (!formData.value.scheduling_professional_id && validProfIds.length === 1) {
    formData.value.scheduling_professional_id = validProfIds[0];
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(
  () => props.initialData,
  init => {
    errorMessage.value = '';
    showPatientDropdown.value = false;

    const initPatientName = init?.patient_name || '';
    const initPatientPhone = init?.patient_phone || '';
    const initPatientEmail = init?.patient_email || '';

    patientSearchQuery.value = initPatientName;
    newPatientPhone.value = initPatientPhone;
    newPatientEmail.value = initPatientEmail;
    isCreatingNewPatient.value = !!(initPatientName && !init?.scheduling_patient_id);

    const sId = init?.scheduling_service_id || init?.service_id || (props.services[0]?.id || null);
    const srv = props.services.find(s => Number(s.id) === Number(sId));

    if (init?.selectedDays && Array.isArray(init.selectedDays)) {
      selectedDays.value = [...init.selectedDays];
    } else if (init?.day_key) {
      selectedDays.value = [init.day_key];
    }
    if (init?.time_slot) {
      formData.value.time_slot = init.time_slot;
      autoSetShiftFromTime(init.time_slot);
    }

    formData.value.scheduling_service_id = sId;
    formData.value.scheduling_professional_id = init?.scheduling_professional_id || init?.professional_id || (props.professionals[0]?.id || null);
    formData.value.price = init?.price !== undefined ? Number(init.price) : (srv?.price || 0);
    formData.value.total_sessions = init?.total_sessions || 10;
    formData.value.due_day = init?.due_day || 5;
    formData.value.notes = init?.notes || '';
    formData.value.status = init?.status || 'active';
    isCustomScheduleByDay.value = !!init?.isCustomScheduleByDay;

    if (init?.session_type === 'trial' || init?.is_trial_class) {
      isTrialClass.value = true;
      formData.value.session_type = 'single';
    } else if (init?.session_type) {
      formData.value.session_type = init.session_type;
    } else if (srv?.billing_type) {
      formData.value.session_type = srv.billing_type;
    } else {
      formData.value.session_type = 'recurring';
    }

    if (init?.scheduleByDay) {
      scheduleByDay.value = { ...init.scheduleByDay };
    } else {
      availableWeekDays.forEach(d => {
        scheduleByDay.value[d.key] = {
          time_slot: init?.time_slot || '08:00',
          professional_id: init?.professional_id || (props.professionals[0]?.id || null),
        };
      });
    }
  },
  { immediate: true }
);

const handleClose = () => {
  const currentDraft = {
    patient_name: patientSearchQuery.value,
    patient_phone: newPatientPhone.value,
    patient_email: newPatientEmail.value,
    scheduling_patient_id: formData.value.scheduling_patient_id,
    scheduling_service_id: formData.value.scheduling_service_id,
    service_id: formData.value.scheduling_service_id,
    scheduling_professional_id: formData.value.scheduling_professional_id,
    professional_id: formData.value.scheduling_professional_id,
    time_slot: formData.value.time_slot,
    selectedDays: selectedDays.value,
    price: formData.value.price,
    total_sessions: formData.value.total_sessions,
    due_day: formData.value.due_day,
    notes: formData.value.notes,
    isCustomScheduleByDay: isCustomScheduleByDay.value,
    scheduleByDay: scheduleByDay.value,
    session_type: formData.value.session_type,
    is_trial_class: isTrialClass.value,
    isDraft: true,
  };
  emit('close', currentDraft);
};

const saveEnrollment = async () => {
  errorMessage.value = '';

  const patientNameTrimmed = patientSearchQuery.value.trim();
  if (!formData.value.scheduling_patient_id && !patientNameTrimmed) {
    errorMessage.value = 'Por favor, digite ou selecione o nome do paciente.';
    return;
  }
  if (!formData.value.scheduling_service_id) {
    errorMessage.value = 'Selecione o serviço.';
    return;
  }
  if (!formData.value.scheduling_professional_id && !isCustomScheduleByDay.value) {
    errorMessage.value = 'Selecione o profissional.';
    return;
  }
  if (selectedDays.value.length === 0) {
    errorMessage.value = 'Selecione pelo menos um dia da semana.';
    return;
  }

  isSaving.value = true;
  try {
    const isTrial = isTrialClass.value;
    const effectiveBillingType = isTrial ? 'single' : (formData.value.session_type === 'recurring' ? 'recurring' : 'package');
    const payload = {
      ...formData.value,
      billing_type: effectiveBillingType,
      session_type: isTrial ? 'trial' : formData.value.session_type,
      total_sessions: isTrial ? 1 : formData.value.total_sessions,
      due_day: formData.value.due_day || 5,
      days_of_week: isTrial ? [selectedDays.value[0] || 'monday'] : selectedDays.value,
      schedule_by_day: isCustomScheduleByDay.value && !isTrial ? scheduleByDay.value : null,
      patient_name: patientNameTrimmed,
      patient_phone: newPatientPhone.value.trim(),
      patient_email: newPatientEmail.value.trim(),
    };

    await schedulingAPI.createEnrollment(payload);
    emit('saved');
    emit('close');
  } catch (err) {
    const errs = err.response?.data?.errors;
    const errText = err.response?.data?.error || (Array.isArray(errs) ? errs.join(', ') : err.response?.data?.message) || 'Erro ao matricular paciente.';
    errorMessage.value = errText;
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
    @click.self="handleClose"
  >
    <div
      class="w-full max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col max-h-[92vh]"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/60">
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-xl flex items-center justify-center text-sm shadow-xs"
            :class="enrollmentType === 'trial' ? 'bg-amber-50 dark:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400' : 'bg-emerald-50 dark:bg-emerald-600/20 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400'"
          >
            {{ enrollmentType === 'trial' ? '⭐' : '📅' }}
          </div>
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>{{ enrollmentType === 'trial' ? 'Agendar Aula Experimental' : 'Nova Matrícula na Grade Fixa' }}</span>
              <span v-if="initialData?.isDraft" class="text-[10px] px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold border border-amber-200 dark:border-amber-500/30">
                📝 Rascunho Restaurado
              </span>
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ enrollmentType === 'trial' ? 'Agende uma aula experimental avulsa para este aluno na grade.' : 'Reserve horários fixos semanais para o aluno/paciente.' }}
            </p>
          </div>
        </div>
        <button type="button" class="text-slate-400 hover:text-slate-600 dark:hover:text-white cursor-pointer" @click="handleClose">✕</button>
      </div>

      <!-- Body -->
      <div class="p-6 overflow-y-auto space-y-4 flex-1">
        <div v-if="errorMessage" class="p-3.5 text-xs bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 rounded-xl flex items-start gap-2.5 shadow-xs">
          <span class="text-base shrink-0">⚠️</span>
          <span class="leading-relaxed font-medium">{{ errorMessage }}</span>
        </div>

        <!-- 1. Patient Input -->
        <div ref="patientContainerRef" class="relative">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              Nome do Paciente <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <span
              v-if="isCreatingNewPatient && !formData.scheduling_patient_id"
              class="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30"
            >
              Novo Paciente
            </span>
          </div>

          <div class="relative">
            <input
              v-model="patientSearchQuery"
              type="text"
              placeholder="Digite o nome do paciente..."
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
              autocomplete="off"
              @focus="showPatientDropdown = true"
              @input="handlePatientInput"
            />
            <span v-if="formData.scheduling_patient_id" class="absolute right-3 top-2.5 text-xs text-emerald-600 dark:text-emerald-400 font-bold">
              ✓ Cadastrado
            </span>
          </div>

          <!-- Dropdown -->
          <div
            v-if="showPatientDropdown && (filteredPatients.length > 0 || patientSearchQuery.trim())"
            class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800"
          >
            <div
              v-for="p in filteredPatients"
              :key="p.id"
              class="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors flex items-center justify-between"
              @click="selectPatient(p)"
            >
              <div>
                <span class="text-xs font-bold text-slate-900 dark:text-white block">{{ p.name }}</span>
                <span class="text-[10px] text-slate-500 dark:text-slate-400">{{ p.phone_number || p.email || 'Sem contato' }}</span>
              </div>
              <span class="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Selecionar</span>
            </div>

            <div
              v-if="patientSearchQuery.trim() && !exactPatientMatch"
              class="p-2.5 bg-blue-50 dark:bg-blue-600/10 hover:bg-blue-100 dark:hover:bg-blue-600/20 cursor-pointer transition-colors flex items-center justify-between text-blue-700 dark:text-blue-300"
              @click="handleQuickCreatePatient"
            >
              <span class="text-xs font-bold flex items-center gap-1">
                <span>+</span> Cadastrar paciente: <strong>"{{ patientSearchQuery }}"</strong>
              </span>
              <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                {{ isQuickCreating ? 'Cadastrando...' : 'Criar Agora' }}
              </span>
            </div>
          </div>

          <!-- Optional Phone & Email -->
          <div
            v-if="isCreatingNewPatient && !formData.scheduling_patient_id"
            class="mt-3 p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2.5"
          >
            <div class="flex items-center justify-between">
              <span class="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <span>👤</span> Dados do novo paciente:
              </span>
              <button
                type="button"
                class="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-2 py-0.5 rounded-md cursor-pointer"
                :disabled="isQuickCreating"
                @click="handleQuickCreatePatient"
              >
                {{ isQuickCreating ? 'Cadastrando...' : '✓ Confirmar Cadastro' }}
              </button>
            </div>
            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">WhatsApp / Telefone</label>
                <input
                  v-model="newPatientPhone"
                  type="text"
                  placeholder="+55 11 99999-9999"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                />
              </div>
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">E-mail</label>
                <input
                  v-model="newPatientEmail"
                  type="email"
                  placeholder="paciente@email.com"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Service & Professional Selection -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Serviço <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <select
              v-model="formData.scheduling_service_id"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white cursor-pointer"
              @change="onServiceChange"
            >
              <option :value="null">Selecione o serviço...</option>
              <option v-for="s in services" :key="s.id" :value="s.id">
                {{ s.name }} ({{ s.attendance_type === 'group' ? '👥 Grupo' : '👤 Particular' }} - {{ s.duration_minutes }} min)
              </option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Profissional <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <select
              v-model="formData.scheduling_professional_id"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white cursor-pointer"
            >
              <option :value="null">Selecione o profissional...</option>
              <option v-for="prof in filteredProfessionals" :key="prof.id" :value="prof.id">
                {{ prof.name }} ({{ prof.specialty || 'Geral' }})
              </option>
            </select>
            <p v-if="formData.scheduling_service_id && filteredProfessionals.length === 0" class="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
              ⚠️ Nenhum profissional habilitado para este serviço.
            </p>
          </div>
        </div>

        <!-- Checkbox de Aula Experimental (Aparece se o serviço permitir) -->
        <div
          v-if="allowsTrialClass"
          class="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl flex items-center justify-between animate-fade-in"
        >
          <label class="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              v-model="isTrialClass"
              type="checkbox"
              class="w-4 h-4 rounded text-amber-500 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 focus:ring-amber-400 cursor-pointer"
              @change="onToggleTrialClass"
            />
            <div>
              <span class="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <span>⭐</span> Aula Experimental
              </span>
              <span class="text-[10px] text-slate-500 dark:text-slate-400 block">
                Trava a matrícula como atendimento avulso/teste de 1 sessão
              </span>
            </div>
          </label>
          <span v-if="isTrialClass" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/40">
            Atendimento Único
          </span>
        </div>

        <!-- Modalidade de Atendimento (se não for Aula Experimental) -->
        <div v-if="!isTrialClass">
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
            Modalidade da Matrícula <span class="text-rose-500 dark:text-red-400">*</span>
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'recurring'
                  ? 'bg-amber-50 dark:bg-amber-600/20 border-amber-500 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'recurring'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>🧘‍♀️</span>
                <span>Mensalidade</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">Vaga fixa perpétua na grade</span>
            </button>

            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'package'
                  ? 'bg-cyan-50 dark:bg-cyan-600/20 border-cyan-500 text-cyan-700 dark:text-cyan-300 ring-1 ring-cyan-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'package'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>🩺</span>
                <span>Pacote de Sessões</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">Total fixo (ex: 10 sessões)</span>
            </button>

            <button
              type="button"
              class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
              :class="
                formData.session_type === 'single'
                  ? 'bg-blue-50 dark:bg-blue-600/20 border-blue-500 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500 shadow-xs'
                  : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              "
              @click="formData.session_type = 'single'"
            >
              <div class="flex items-center gap-1.5 font-bold text-xs">
                <span>⚡</span>
                <span>Sessão Avulsa</span>
              </div>
              <span class="text-[10px] font-normal text-slate-500 dark:text-slate-400">1 atendimento pontual</span>
            </button>
          </div>
        </div>

        <!-- 3. Week Days Selection -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              {{ isTrialClass ? 'Dia da Semana da Aula Experimental' : 'Dias Fixos da Semana' }} <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <span v-if="selectedService?.custom_schedule_enabled" class="text-[10px] text-purple-600 dark:text-purple-300 font-semibold flex items-center gap-1">
              <span>⏰</span> Dias exclusivos do serviço
            </span>
          </div>

          <div class="grid grid-cols-6 gap-1.5">
            <button
              v-for="d in availableWeekDays"
              :key="d.key"
              type="button"
              class="py-2 text-xs font-bold rounded-xl border transition-all text-center"
              :class="[
                !isDayAllowedForService(d.key)
                  ? 'border-slate-200 dark:border-slate-800/80 bg-slate-100 dark:bg-slate-950/40 text-slate-400 dark:text-slate-600 opacity-40 cursor-not-allowed'
                  : (selectedDays.includes(d.key)
                    ? (formData.session_type === 'recurring' ? 'bg-amber-600 border-amber-500 text-white shadow-xs cursor-pointer' : 'bg-blue-600 border-blue-500 text-white shadow-xs cursor-pointer')
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-slate-200 cursor-pointer')
              ]"
              :disabled="!isDayAllowedForService(d.key)"
              :title="!isDayAllowedForService(d.key) ? 'Dia fora da escala deste serviço' : d.full"
              @click="toggleDay(d.key)"
            >
              {{ d.label }}
            </button>
          </div>
        </div>

        <!-- Toggle: Horários Flexíveis por Dia (Apenas quando não for experimental) -->
        <div v-if="!isTrialClass && selectedDays.length > 1" class="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl">
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              v-model="isCustomScheduleByDay"
              type="checkbox"
              class="w-4 h-4 rounded text-blue-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-blue-500 cursor-pointer"
            />
            <span class="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Definir horários ou profissionais diferentes por dia
            </span>
          </label>
          <span class="text-[10px] text-slate-500 dark:text-slate-400">
            {{ isCustomScheduleByDay ? 'Modo Flexível Ativo' : 'Mesmo horário para todos' }}
          </span>
        </div>

        <!-- 4.1 Custom Schedule per Selected Day -->
        <div v-if="!isTrialClass && isCustomScheduleByDay" class="space-y-2.5 animate-fade-in">
          <div
            v-for="dayKey in selectedDays"
            :key="dayKey"
            class="p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-2"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-blue-600 dark:text-blue-400 capitalize">
                🗓️ {{ getDayLabel(dayKey) }}
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Horário</label>
                <input
                  v-model="scheduleByDay[dayKey].time_slot"
                  type="text"
                  placeholder="08:00"
                  class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label class="block text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">Profissional</label>
                <select
                  v-model="scheduleByDay[dayKey].professional_id"
                  class="w-full h-8 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option v-for="prof in filteredProfessionals" :key="prof.id" :value="prof.id">
                    {{ prof.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- 4.2 Standard Fixed Time Slot Selection -->
        <div v-else class="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-2.5">
          <div class="flex items-center justify-between">
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
              {{ isTrialClass ? 'Horário da Aula Experimental' : 'Horário Fixo' }} <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <div class="flex items-center gap-1 p-0.5 bg-slate-200/60 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-lg">
              <button
                type="button"
                class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                :class="activeShift === 'morning' ? 'bg-blue-600 text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                @click="activeShift = 'morning'"
              >
                🌅 Manhã
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                :class="activeShift === 'afternoon' ? 'bg-blue-600 text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                @click="activeShift = 'afternoon'"
              >
                ☀️ Tarde
              </button>
              <button
                type="button"
                class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                :class="activeShift === 'night' ? 'bg-blue-600 text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                @click="activeShift = 'night'"
              >
                🌙 Noite
              </button>
            </div>
          </div>

          <div class="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            <button
              v-for="t in shiftTimes"
              :key="t"
              type="button"
              class="py-1.5 px-2 text-xs font-mono font-bold rounded-xl border transition-all text-center"
              :class="[
                !isTimeSlotAllowedForService(t)
                  ? 'border-slate-200 dark:border-slate-800/80 bg-slate-100 dark:bg-slate-950/40 text-slate-400 dark:text-slate-600 opacity-40 cursor-not-allowed'
                  : (formData.time_slot === t
                    ? 'bg-blue-600 border-blue-500 text-white shadow-md cursor-pointer'
                    : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-700 dark:text-slate-300 cursor-pointer')
              ]"
              :disabled="!isTimeSlotAllowedForService(t)"
              :title="!isTimeSlotAllowedForService(t) ? 'Horário fora da escala deste serviço' : t"
              @click="formData.time_slot = t"
            >
              {{ t }}
            </button>
          </div>
        </div>

        <!-- 5. CONFIGURAÇÃO ESPECÍFICA POR MODALIDADE -->
        <!-- MENSALIDADE RECORRENTE (PILATES) -->
        <div
          v-if="!isTrialClass && formData.session_type === 'recurring'"
          class="p-4 bg-slate-50 dark:bg-slate-950/90 rounded-2xl border border-amber-200 dark:border-amber-500/30 space-y-3.5 animate-fade-in"
        >
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
              <span>🧘‍♀️</span> Matrícula Recorrente & Vaga Fixa
            </span>
            <span class="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Renovação mensal contínua
            </span>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Dia de Vencimento da Mensalidade
              </label>
              <div class="flex items-center gap-1">
                <button
                  v-for="d in [5, 10, 15, 20]"
                  :key="d"
                  type="button"
                  class="px-2 py-1 text-xs font-bold font-mono rounded-lg border transition-all cursor-pointer"
                  :class="formData.due_day === d ? 'bg-amber-600 border-amber-500 text-white shadow-xs' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'"
                  @click="formData.due_day = d"
                >
                  {{ String(d).padStart(2, '0') }}
                </button>
                <input
                  v-model.number="formData.due_day"
                  type="number"
                  min="1"
                  max="31"
                  class="w-12 h-7 px-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white font-mono text-center focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
            </div>

            <div>
              <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Data de Início das Aulas
              </label>
              <input
                v-model="formData.start_date"
                type="date"
                class="w-full h-8 px-2.5 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <!-- PACOTE DE SESSÕES (FISIOTERAPIA) -->
        <div
          v-else-if="!isTrialClass && formData.session_type === 'package'"
          class="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-950/80 border border-cyan-200 dark:border-cyan-500/30 rounded-2xl animate-fade-in"
        >
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Total de Sessões do Pacote <span class="text-rose-500 dark:text-red-400">*</span>
            </label>
            <input
              v-model.number="formData.total_sessions"
              type="number"
              min="1"
              max="100"
              class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Data de Início
            </label>
            <input
              v-model="formData.start_date"
              type="date"
              class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- AULA EXPERIMENTAL OU AVULSA -->
        <div v-else class="p-3 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl animate-fade-in">
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Data do Atendimento <span class="text-rose-500 dark:text-red-400">*</span>
          </label>
          <input
            v-model="formData.start_date"
            type="date"
            class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <!-- 6. Price and Notes -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                {{ enrollmentType === 'trial' ? 'Valor da Aula' : 'Valor Mensal / Pacote (R$)' }}
              </label>
              <span
                v-if="enrollmentType === 'trial'"
                class="text-[9px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 px-1.5 py-0.2 rounded-md"
              >
                Gratuito (R$ 0,00)
              </span>
            </div>

            <input
              v-if="enrollmentType === 'package'"
              v-model.number="formData.price"
              type="number"
              step="0.01"
              placeholder="0.00"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white font-mono focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
            <div
              v-else
              class="w-full h-9 px-3 text-xs bg-slate-100 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-600 dark:text-slate-400 font-mono flex items-center justify-between cursor-not-allowed select-none"
              title="Aulas experimentais não possuem cobrança (valor zero para fins de cálculo e relatórios)"
            >
              <span class="text-amber-600 dark:text-amber-400 font-bold">R$ 0,00</span>
              <span class="text-[10px] text-slate-500 font-sans">Sem custo (Cortesia)</span>
            </div>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Observações Internas</label>
            <input
              v-model="formData.notes"
              type="text"
              :placeholder="enrollmentType === 'trial' ? 'Ex: Aula experimental para conhecer o estúdio...' : 'Ex: Aluno novo, foco em postura...'"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
            />
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-end gap-2.5">
        <button type="button" class="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer" @click="handleClose">
          Cancelar
        </button>
        <button
          type="button"
          class="px-5 py-2 text-xs font-bold text-white rounded-xl shadow-md cursor-pointer disabled:opacity-50 transition-all"
          :class="enrollmentType === 'trial' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-blue-600 hover:bg-blue-500'"
          :disabled="isSaving"
          @click="saveEnrollment"
        >
          {{ isSaving ? (enrollmentType === 'trial' ? 'Agendando...' : 'Matriculando...') : (enrollmentType === 'trial' ? 'Confirmar Aula Experimental' : 'Confirmar Matrícula na Grade') }}
        </button>
      </div>
    </div>
  </div>
</template>
